import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, Tooltip,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import Save from '@mui/icons-material/Save';
import api from '../api/axios';
import { AuthContext } from '../AuthContext';
import { mandantUrl, useBusinessContext } from '../BusinessContext';
import { canReadEinrichtungen, canManageEinrichtungen } from '../components/einrichtungAccess';
import PageHeader from '../components/PageHeader';
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';
import { documentStatusChipSx } from '../components/documentStatusChip';

const emptyForm = () => ({ Name: '', Typ: '', Status: 'Aktiv', Breitengrad: '', Laengengrad: '', Bemerkung: '' });
const columns = [['Name', 'Name'], ['Typ', 'Typ'], ['Status', 'Status'], ['Koordinaten', 'Koordinaten'], ['Bemerkung', 'Bemerkung']];
const coordinate = value => value == null ? '–' : Number(value).toLocaleString('de-DE', { maximumFractionDigits: 7 });
const scopedUrl = (url, mandantId, betriebId) => `${mandantUrl(url, mandantId)}&betrieb_id=${encodeURIComponent(betriebId)}`;

function validation(form) {
  if (!form.Name.trim() || form.Name.trim().length > 100) return 'Name ist erforderlich (maximal 100 Zeichen).';
  if (!form.Typ || !form.Status) return 'Bitte Typ und Status auswählen.';
  const latitude = String(form.Breitengrad).trim(), longitude = String(form.Laengengrad).trim();
  if (Boolean(latitude) !== Boolean(longitude)) return 'Breitengrad und Längengrad müssen gemeinsam angegeben werden.';
  for (const [raw, label, limit] of [[latitude, 'Breitengrad', 90], [longitude, 'Längengrad', 180]]) {
    if (!raw) continue;
    const value = Number(raw.replace(',', '.'));
    if (!Number.isFinite(value) || value < -limit || value > limit) return `${label} muss zwischen ${-limit} und ${limit} liegen.`;
  }
  return '';
}

export default function Einrichtungen() {
  const { mandantId, betriebId } = useBusinessContext();
  const auth = useContext(AuthContext);
  const read = canReadEinrichtungen(auth?.user), write = canManageEinrichtungen(auth?.user);
  const scopeState = useRef(null), requests = useRef(0);
  const [items, setItems] = useState([]), [options, setOptions] = useState({ typen: [], statuswerte: [] });
  const [loading, setLoading] = useState(false), [loadError, setLoadError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false), [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm), [saving, setSaving] = useState(false);
  const [typeInput, setTypeInput] = useState('');
  const [dialogError, setDialogError] = useState(''), [message, setMessage] = useState('');
  const [search, setSearch] = useState(''), [typeFilter, setTypeFilter] = useState(''), [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState({ field: 'Name', descending: false });

  const load = useCallback(async () => {
    const scope = scopeState.current;
    if (!read || !mandantId || !betriebId || !scope?.active
      || scope.mandantId !== mandantId || scope.betriebId !== betriebId) return false;
    const job = ++requests.current;
    setLoading(true); setLoadError('');
    try {
      const [list, choices] = await Promise.all([
        api.get(scopedUrl('/einrichtungen', mandantId, betriebId)),
        api.get(scopedUrl('/einrichtungen/optionen', mandantId, betriebId)),
      ]);
      if (!scope.active || job !== requests.current) return false;
      setItems(list.data); setOptions(choices.data);
      return true;
    } catch (error) {
      if (scope.active && job === requests.current) setLoadError(error.response?.data?.error || 'Einrichtungen konnten nicht geladen werden.');
      return false;
    } finally { if (scope.active && job === requests.current) setLoading(false); }
  }, [mandantId, betriebId, read]);

  useEffect(() => {
    const scope = { mandantId, betriebId, active: true }; scopeState.current = scope;
    setItems([]); setOptions({ typen: [], statuswerte: [] }); setLoading(false); setLoadError('');
    setDialogOpen(false); setSelected(null); setSaving(false); setMessage('');
    setSearch(''); setTypeFilter(''); setStatusFilter('');
    load();
    return () => { scope.active = false; };
  }, [load, mandantId, betriebId, write]);

  const visible = useMemo(() => items.filter(item =>
    item.Name.toLocaleLowerCase('de-DE').includes(search.trim().toLocaleLowerCase('de-DE'))
      && (!typeFilter || item.Typ === typeFilter) && (!statusFilter || item.Status === statusFilter))
    .sort((a, b) => {
      const order = sort.field === 'Koordinaten'
        ? (a.Breitengrad == null ? (b.Breitengrad == null ? 0 : -1) : b.Breitengrad == null ? 1
          : Number(a.Breitengrad) - Number(b.Breitengrad) || Number(a.Laengengrad) - Number(b.Laengengrad))
        : String(a[sort.field] ?? '').localeCompare(String(b[sort.field] ?? ''), 'de', { numeric: true });
      return order * (sort.descending ? -1 : 1);
    }), [items, search, typeFilter, statusFilter, sort]);

  const openDialog = (item = null) => {
    setSelected(item);
    setForm(item ? Object.fromEntries(Object.keys(emptyForm()).map(key => [key, item[key] ?? ''])) : emptyForm());
    setTypeInput(item?.Typ || '');
    setDialogError(''); setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setSelected(null); };
  const change = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const save = async () => {
    if (saving || !write || !mandantId || !betriebId) return;
    const invalid = validation(form);
    if (invalid) { setDialogError(invalid); return; }
    const scope = scopeState.current;
    if (!scope?.active || scope.mandantId !== mandantId || scope.betriebId !== betriebId) return;
    setSaving(true); setDialogError('');
    const data = { ...form, Name: form.Name.trim(),
      Breitengrad: String(form.Breitengrad).trim() || null,
      Laengengrad: String(form.Laengengrad).trim() || null, Bemerkung: form.Bemerkung || null };
    try {
      if (selected) await api.put(scopedUrl(`/einrichtungen/${selected.Id}`, mandantId, betriebId), data);
      else await api.post(scopedUrl('/einrichtungen', mandantId, betriebId), data);
      if (!scope.active) return;
      closeDialog();
      if (await load()) setMessage('Einrichtung erfolgreich gespeichert.');
    } catch (error) {
      if (scope.active) setDialogError(error.response?.data?.error || 'Einrichtung konnte nicht gespeichert werden.');
    } finally { if (scope.active) setSaving(false); }
  };

  if (!read) return <Alert severity="warning">Keine Berechtigung für Einrichtungen.</Alert>;
  return <Box>
    <PageHeader title="Einrichtungen" action={write && <Button variant="contained" size="small" startIcon={<Add />}
      disabled={!mandantId || !betriebId || loading || !options.typen.length} onClick={() => openDialog()}>Neue Einrichtung</Button>} />
    {(!mandantId || !betriebId) && <Alert severity="info">Bitte einen Mandanten und Betrieb auswählen.</Alert>}
    {loading && <LoadingIndicator label="Einrichtungen werden geladen …" />}
    {loadError && <LoadError onRetry={load}>{loadError}</LoadError>}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
      <TextField size="small" label="Name suchen" value={search} onChange={event => setSearch(event.target.value)} />
      <TextField size="small" select label="Typ filtern" value={typeFilter} onChange={event => setTypeFilter(event.target.value)} sx={{ minWidth: 180 }}>
        <MenuItem value="">Alle Typen</MenuItem>{options.typen.map(kind => <MenuItem key={kind} value={kind}>{kind}</MenuItem>)}
      </TextField>
      <TextField size="small" select label="Status filtern" value={statusFilter} onChange={event => setStatusFilter(event.target.value)} sx={{ minWidth: 160 }}>
        <MenuItem value="">Alle Statuswerte</MenuItem>{options.statuswerte.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
      </TextField>
    </Stack>
    <TableContainer component={Paper}><Table size="small" aria-label="Einrichtungen"><TableHead><TableRow>
      {columns.map(([field, label]) => <TableCell key={field} sortDirection={sort.field === field ? (sort.descending ? 'desc' : 'asc') : false}>
        <TableSortLabel active={sort.field === field} direction={sort.descending ? 'desc' : 'asc'}
          onClick={() => setSort({ field, descending: sort.field === field ? !sort.descending : false })}>{label}</TableSortLabel>
      </TableCell>)}
      <TableCell align="right">Aktion</TableCell>
    </TableRow></TableHead><TableBody>
      {!loading && !loadError && mandantId && betriebId && !visible.length && <TableRow><TableCell colSpan={6} align="center">Keine Einrichtungen gefunden.</TableCell></TableRow>}
      {visible.map(item => <TableRow key={item.Id} hover>
        <TableCell>{item.Name}</TableCell><TableCell>{item.Typ}</TableCell>
        <TableCell><Chip size="small" label={item.Status}
          sx={documentStatusChipSx(item.Status === 'Aktiv' ? 'FREIGEGEBEN' : 'STILLGELEGT')} /></TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.Breitengrad == null ? '–' : `${coordinate(item.Breitengrad)}, ${coordinate(item.Laengengrad)}`}</TableCell>
        <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxWidth: 360 }}>{item.Bemerkung || '–'}</TableCell>
        <TableCell align="right">{write && <Tooltip title="Bearbeiten"><IconButton size="small" color="primary"
          aria-label={`Einrichtung ${item.Name} bearbeiten`} onClick={() => openDialog(item)}><Edit fontSize="small" /></IconButton></Tooltip>}</TableCell>
      </TableRow>)}
    </TableBody></Table></TableContainer>
    {write && dialogOpen && <Dialog open onClose={saving ? undefined : closeDialog} maxWidth="sm" fullWidth>
      <DialogTitle>{selected ? 'Einrichtung bearbeiten' : 'Neue Einrichtung'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {dialogError && <Alert severity="error">{dialogError}</Alert>}
        <TextField size="small" label="Name" required value={form.Name} disabled={saving}
          slotProps={{ htmlInput: { maxLength: 100 } }} onChange={event => change('Name', event.target.value)} />
        <Autocomplete options={options.typen} value={form.Typ || null} inputValue={typeInput}
          disabled={saving} freeSolo={false}
          onInputChange={(_event, value, reason) => {
            setTypeInput(value);
            if (reason === 'input' || reason === 'clear') change('Typ', '');
          }}
          onChange={(_event, value) => { change('Typ', value || ''); setTypeInput(value || ''); }}
          renderInput={params => <TextField {...params} size="small" label="Typ" required />} />
        <TextField size="small" select label="Status" required value={form.Status} disabled={saving} onChange={event => change('Status', event.target.value)}>
          {options.statuswerte.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
        </TextField>
        <TextField size="small" type="number" label="Breitengrad" value={form.Breitengrad} disabled={saving}
          helperText="WGS84: −90 bis 90, maximal 7 Nachkommastellen"
          slotProps={{ htmlInput: { min: -90, max: 90, step: '0.0000001' } }} onChange={event => change('Breitengrad', event.target.value)} />
        <TextField size="small" type="number" label="Längengrad" value={form.Laengengrad} disabled={saving}
          helperText="WGS84: −180 bis 180, gemeinsam mit Breitengrad angeben"
          slotProps={{ htmlInput: { min: -180, max: 180, step: '0.0000001' } }} onChange={event => change('Laengengrad', event.target.value)} />
        <TextField size="small" label="Bemerkung" multiline minRows={3} value={form.Bemerkung} disabled={saving} onChange={event => change('Bemerkung', event.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" startIcon={<Close />} onClick={closeDialog} disabled={saving}>Abbrechen</Button>
        <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>{saving ? 'Wird gespeichert …' : 'Speichern'}</Button>
      </DialogActions>
    </Dialog>}
    <Snackbar open={Boolean(message)} autoHideDuration={4000} onClose={() => setMessage('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert severity="success" variant="filled">{message}</Alert>
    </Snackbar>
  </Box>;
}
